import { useTranslations } from "next-intl"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Button } from "@/components/ui/button"
import { SidebarTrigger } from "@/components/ui/sidebar"
import { Menu, LogOut } from "lucide-react"
import { LanguageSelector } from "@/app/components/LanguageSelector"
import { getInitials } from "@/lib/utils"
import { useAuthStore } from "@/stores/authStore"
import { useNavigate } from "react-router-dom"

export function NavHeader() {
	const t = useTranslations()
	const navigate = useNavigate()
	const user = useAuthStore((s) => s.user)
	const company = useAuthStore((s) => s.company)
	const logout = useAuthStore((s) => s.logout)

	const handleLogout = () => {
		logout()
		navigate("/login", { replace: true })
	}

	return (
		<header
			className="sticky top-0 z-50 flex h-14 shrink-0 items-center gap-2 border-b border-[#e5e7eb] px-4 bg-white backdrop-blur-md"
			style={{ backgroundImage: "linear-gradient(to right, rgba(45, 92, 63, 0.05), rgba(61, 122, 79, 0.05), rgba(74, 153, 96, 0.05))" }}
		>
			<SidebarTrigger className="md:hidden mr-2">
				<Menu className="h-5 w-5" />
			</SidebarTrigger>

			<div className="flex flex-1 items-center gap-4">
				{company?.logo_url && (
					<img src={company.logo_url} alt={company.name} className="h-10 w-10 object-contain rounded-md" />
				)}
				<div>
					<h1 className="text-xl font-bold bg-gradient-to-r from-[#2d5c3f] via-[#3d7a4f] to-[#4a9960] bg-clip-text text-transparent">
						{t("header.title")}
					</h1>
					<p className="text-sm text-muted-foreground">{t("header.subtitle")}</p>
				</div>
			</div>

			<div className="flex items-center gap-4">
				<div className="hidden md:flex">
					<LanguageSelector compact={true} />
				</div>
				<div className="hidden md:block h-8 w-px bg-[#1e3a5f]/10 mx-1" />

				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button variant="ghost" className="relative h-10 w-10 rounded-full hover:bg-[#1e3a5f]/5 transition-colors">
							<Avatar className="h-10 w-10 border-2 border-[#1e3a5f]/20 hover:border-[#1e3a5f]/40 transition-colors shadow-sm">
								<AvatarImage src="" alt={user?.name || "User"} />
								<AvatarFallback className="bg-gradient-to-br from-[#1e3a5f]/10 to-[#2d5c3f]/10 text-[#1e3a5f] font-semibold text-sm">
									{getInitials(user?.name || "User")}
								</AvatarFallback>
							</Avatar>
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end" className="w-56 shadow-lg border-[#1e3a5f]/10">
						<DropdownMenuLabel className="pb-2">
							<div className="flex flex-col space-y-1">
								<p className="text-sm font-semibold leading-none text-[#1e3a5f]">{user?.name || "User"}</p>
								<p className="text-xs leading-none text-muted-foreground">{user?.email || ""}</p>
								<Badge variant="info" className="mt-2 w-fit text-[10px] uppercase tracking-wider">
									{(user?.role || "user").toUpperCase()}
								</Badge>
							</div>
						</DropdownMenuLabel>
						<DropdownMenuSeparator className="bg-[#1e3a5f]/10" />
						<DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive cursor-pointer">
							<LogOut className="mr-2 h-4 w-4" />
							{t("userMenu.logout")}
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</div>
		</header>
	)
}
